// Must not import React: the on-screen picker and the PDF renderer share this
// math so the printed diagram matches what the doctor placed on screen.

import type { ElasticPlacement } from './model';
import { TOOTH_RADIUS, toothButtonAnchor, toothCenter, toothLabel } from './toothGeometry';

/** How far the two strands of a 2-tooth band bow apart (px). */
const BAND_BOW = 15;

type Pt = { x: number; y: number };

/** Unit vector and perpendicular for a segment, plus its midpoint. */
function segMetrics(a: Pt, b: Pt) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    return { ux, uy, px: -uy, py: ux, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
}

/** Cubic strands of a 2-tooth loop ([P0..P3]), shared by the band path and the
 *  direction arrows so they always sit on the same curves. */
function loopStrands(a: Pt, b: Pt): { out: Pt[]; back: Pt[] } {
    const { ux, uy, px, py } = segMetrics(a, b);
    const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    // Pull control points inward along the axis so each strand rounds off near
    // the hooks instead of coming to a point - an oval loop, not a lens.
    const inset = Math.min(len * 0.42, 26);
    return {
        out: [
            a,
            { x: a.x + ux * inset + px * BAND_BOW, y: a.y + uy * inset + py * BAND_BOW },
            { x: b.x - ux * inset + px * BAND_BOW, y: b.y - uy * inset + py * BAND_BOW },
            b
        ],
        back: [
            b,
            { x: b.x - ux * inset - px * BAND_BOW, y: b.y - uy * inset - py * BAND_BOW },
            { x: a.x + ux * inset - px * BAND_BOW, y: a.y + uy * inset - py * BAND_BOW },
            a
        ]
    };
}

/** Point on a cubic Bézier at parameter t. */
function cubicAt(c: Pt[], t: number): Pt {
    const mt = 1 - t;
    const w0 = mt * mt * mt;
    const w1 = 3 * mt * mt * t;
    const w2 = 3 * mt * t * t;
    const w3 = t * t * t;
    return {
        x: w0 * c[0].x + w1 * c[1].x + w2 * c[2].x + w3 * c[3].x,
        y: w0 * c[0].y + w1 * c[1].y + w2 * c[2].y + w3 * c[3].y
    };
}

/** Unit tangent of a cubic Bézier at parameter t. */
function cubicTangent(c: Pt[], t: number): Pt {
    const mt = 1 - t;
    const dx =
        3 * mt * mt * (c[1].x - c[0].x) +
        6 * mt * t * (c[2].x - c[1].x) +
        3 * t * t * (c[3].x - c[2].x);
    const dy =
        3 * mt * mt * (c[1].y - c[0].y) +
        6 * mt * t * (c[2].y - c[1].y) +
        3 * t * t * (c[3].y - c[2].y);
    const len = Math.hypot(dx, dy) || 1;
    return { x: dx / len, y: dy / len };
}

/** 2 teeth draw a there-and-back loop so it reads like a real elastic; 3+ draw a
 *  closed polygon; '' when fewer than 2 points resolve. */
export function bandPathD(points: Array<{ x: number; y: number }>): string {
    if (points.length < 2) return '';
    if (points.length === 2) {
        const { out, back } = loopStrands(points[0], points[1]);
        const f = (n: number) => n.toFixed(2);
        return (
            `M ${f(out[0].x)} ${f(out[0].y)} ` +
            `C ${f(out[1].x)} ${f(out[1].y)} ${f(out[2].x)} ${f(out[2].y)} ${f(out[3].x)} ${f(out[3].y)} ` +
            `C ${f(back[1].x)} ${f(back[1].y)} ${f(back[2].x)} ${f(back[2].y)} ${f(back[3].x)} ${f(back[3].y)} Z`
        );
    }
    let d = `M ${points[0].x} ${points[0].y} `;
    for (let i = 1; i < points.length; i++) {
        d += `L ${points[i].x} ${points[i].y} `;
    }
    d += `L ${points[0].x} ${points[0].y}`;
    return d;
}

/** Resolve a placement's teeth to their attachment anchors (the buttons), in
 *  install order - so the band lines pass through the buttons. */
export function placementPoints(placement: ElasticPlacement): Array<{ x: number; y: number }> {
    return placement.teeth
        .filter((fdi) => toothCenter(fdi) !== undefined)
        .map((fdi) => toothButtonAnchor(fdi));
}

/** Average position of a placement's teeth. */
export function placementCentroid(points: Array<{ x: number; y: number }>): {
    x: number;
    y: number;
} {
    if (points.length === 0) return { x: 0, y: 0 };
    const sum = points.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
    return { x: sum.x / points.length, y: sum.y / points.length };
}

/** Filled-triangle arrowhead centered at (cx, cy), pointing along (ux, uy). */
function arrowGlyph(cx: number, cy: number, ux: number, uy: number): string {
    const size = 13;
    const half = 7;
    const tipX = cx + ux * size * 0.5;
    const tipY = cy + uy * size * 0.5;
    const baseX = cx - ux * size * 0.5;
    const baseY = cy - uy * size * 0.5;
    const px = -uy * half;
    const py = ux * half;
    return `M ${tipX.toFixed(2)} ${tipY.toFixed(2)} L ${(baseX + px).toFixed(2)} ${(baseY + py).toFixed(2)} L ${(baseX - px).toFixed(2)} ${(baseY - py).toFixed(2)} Z`;
}

/** Arrowhead at the edge of the destination tooth, pointing along `from → to`. */
export function arrowHeadPath(
    from: { x: number; y: number },
    to: { x: number; y: number }
): string {
    const { ux, uy } = segMetrics(from, to);
    const backoff = TOOTH_RADIUS + 3;
    return arrowGlyph(to.x - ux * (backoff + 4.5), to.y - uy * (backoff + 4.5), ux, uy);
}

/** Install-direction arrows: one per loop strand for 2 teeth, else one per
 *  consecutive tooth pair. */
export function placementArrowHeads(placement: ElasticPlacement): string[] {
    const pts = placementPoints(placement);
    if (pts.length < 2) return [];
    if (pts.length === 2) {
        const { out, back } = loopStrands(pts[0], pts[1]);
        // Past each strand's midpoint so the outgoing and return arrows never overlap.
        const t = 0.62;
        const po = cubicAt(out, t);
        const pb = cubicAt(back, t);
        const to = cubicTangent(out, t);
        const tb = cubicTangent(back, t);
        return [
            arrowGlyph(po.x, po.y, to.x, to.y), // outgoing strand → b
            arrowGlyph(pb.x, pb.y, tb.x, tb.y) // return strand → a
        ];
    }
    const heads: string[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
        heads.push(arrowHeadPath(pts[i], pts[i + 1]));
    }
    return heads;
}

/** Starting anchor of a placement (the first tooth in install order). The
 *  numbered start hook is drawn here - it marks both order and where to begin. */
export function placementStartPoint(
    placement: ElasticPlacement
): { x: number; y: number } | undefined {
    return placementPoints(placement)[0];
}

/** Minimum centre-to-centre gap between two order-number badges (px). */
const BADGE_MIN_GAP = 28;

/** Badges start at the band centroid; overlapping ones (e.g. a cross and a box
 *  sharing a centre) are nudged up/down until no number hides another. */
export function orderBadgePositions(
    placements: ElasticPlacement[]
): Array<{ x: number; y: number }> {
    const placed: Array<{ x: number; y: number }> = [];
    for (const placement of placements) {
        const base = placementCentroid(placementPoints(placement));
        let pos = { ...base };
        let step = 1;
        // Nudge ±y by increasing amounts until it no longer collides with an
        // earlier badge (fans coincident badges into a small vertical stack).
        while (
            placed.some((q) => Math.hypot(q.x - pos.x, q.y - pos.y) < BADGE_MIN_GAP) &&
            step <= 12
        ) {
            const offset = Math.ceil(step / 2) * BADGE_MIN_GAP;
            pos = { x: base.x, y: base.y + (step % 2 === 1 ? offset : -offset) };
            step += 1;
        }
        placed.push(pos);
    }
    return placed;
}

/** Round-trip label since every band returns to its start tooth, e.g. "27 → 44 → 27";
 *  pass an ASCII separator for PDFs (Helvetica has no "→" glyph). */
export function closedTeethLabel(teeth: number[], separator = ' → '): string {
    const seq = teeth.length < 2 ? teeth : [...teeth, teeth[0]];
    return seq.map(toothLabel).join(separator);
}

/** Human-readable one-liner, e.g. "Class II: 16 – 46 · 3/16 6-6.5oz Heavy". */
export function summarizePlacement(placement: ElasticPlacement): string {
    const name = placement.name.trim() || 'Elastic';
    const teeth = placement.teeth.map(toothLabel).join(' – ');
    const base = `${name}: ${teeth}`;
    return placement.size ? `${base} · ${placement.size}` : base;
}
